export type BestBuySelectionMatch = {
  supplierId?: unknown;
  shipperId?: unknown;
  unitPrice?: number | null;
};

export type BestBuySelectableItem = {
  _id: string;
  unitPrice: number;
  supplierContract: {
    _id: string;
    pinCode?: string | null;
    supplierId?: unknown;
    shipperId?: unknown;
  };
};

const sameId = (left?: unknown, right?: unknown) =>
  left != null && right != null && String(left) === String(right);

const hasPin = (pin?: string | null): pin is string => Boolean(pin);

const orderBySelectedIds = <T extends BestBuySelectableItem>(
  list: T[],
  selectedIds: string[]
): T[] => {
  const selectedIdSet = new Set(selectedIds);
  const selected = selectedIds
    .map((id) => list.find((item) => item.supplierContract._id === id))
    .filter((item): item is T => Boolean(item));
  const rest = list.filter(
    (item) => !selectedIdSet.has(item.supplierContract._id)
  );
  return [...selected, ...rest];
};

const resolveByContractIds = <T extends BestBuySelectableItem>(
  list: T[],
  selectedContractIds: string[]
): T[] =>
  selectedContractIds
    .map((id) => list.find((item) => item.supplierContract._id === id))
    .filter((item): item is T => Boolean(item));

const resolvePrimaryByMatch = <T extends BestBuySelectableItem>(
  list: T[],
  primaryPin: string,
  match?: BestBuySelectionMatch
): T | undefined => {
  let candidates = list.filter(
    (item) => item.supplierContract.pinCode === primaryPin
  );

  if (match?.supplierId != null && match?.shipperId != null) {
    const narrowed = candidates.filter(
      (item) =>
        sameId(item.supplierContract.supplierId, match.supplierId) &&
        sameId(item.supplierContract.shipperId, match.shipperId)
    );
    if (narrowed.length) {
      candidates = narrowed;
    }
  }

  if (candidates.length > 1 && match?.unitPrice != null) {
    const narrowed = candidates.filter(
      (item) => item.unitPrice === match.unitPrice
    );
    if (narrowed.length) {
      candidates = narrowed;
    }
  }

  return candidates[0];
};

const resolveAlternatives = <T extends BestBuySelectableItem>(
  list: T[],
  alternativePins: string[],
  alreadySelectedIds: Set<string>
): T[] => {
  const selected: T[] = [];
  const taken = new Set(alreadySelectedIds);

  for (const pin of alternativePins) {
    const match = list.find(
      (item) =>
        item.supplierContract.pinCode === pin &&
        !taken.has(item.supplierContract._id)
    );
    if (match) {
      selected.push(match);
      taken.add(match.supplierContract._id);
    }
  }

  return selected;
};

export const resolveBestBuySelection = <T extends BestBuySelectableItem>({
  list,
  selectedContractIds,
  pinCodeOrder,
  selectedContractMatch,
  defaultSelected,
}: {
  list: T[];
  selectedContractIds?: string[];
  pinCodeOrder?: string[];
  selectedContractMatch?: BestBuySelectionMatch;
  defaultSelected?: T;
}): { orderedData: T[]; selectedItems: T[] | undefined } => {
  const byIds = selectedContractIds?.length
    ? resolveByContractIds(list, selectedContractIds)
    : [];

  if (byIds.length) {
    const selectedIds = byIds.map((item) => item.supplierContract._id);
    return {
      orderedData: orderBySelectedIds(list, selectedIds),
      selectedItems: byIds,
    };
  }

  const pins = (pinCodeOrder || []).filter(hasPin);

  if (pins.length) {
    const [primaryPin, ...alternativePins] = pins;
    const primary = resolvePrimaryByMatch(
      list,
      primaryPin,
      selectedContractMatch
    );
    const selectedIds = new Set(
      primary ? [primary.supplierContract._id] : []
    );
    const alternatives = resolveAlternatives(
      list,
      alternativePins,
      selectedIds
    );
    const selectedItems = primary ? [primary, ...alternatives] : alternatives;

    if (selectedItems.length) {
      return {
        orderedData: orderBySelectedIds(
          list,
          selectedItems.map((item) => item.supplierContract._id)
        ),
        selectedItems,
      };
    }
  }

  const fallback = defaultSelected
    ? list.find(
        (item) =>
          item.supplierContract._id === defaultSelected.supplierContract._id
      ) ?? { ...defaultSelected, _id: defaultSelected.supplierContract._id }
    : undefined;

  return {
    orderedData: list,
    selectedItems: fallback ? [fallback] : undefined,
  };
};
